import { FC, use, useState } from 'react';
import {
  AccordionSelect,
  AsyncAutocomplete,
  CurrencyInput,
} from '@ghosts-inc/design-system/components';
import { Cross } from '@ghosts-inc/design-system/icons';
import { Theme, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { analytics } from '@/analytics/segment';
import { DialogPaperSxProps } from '@/legacy/common/confirm/ConfirmModal';
import { FlatBuyerPreferences } from '../../_components/types';
import { PercentOffMsrpSelector } from '../../preferences/_components/PercentOffMsrpSelector';
import { searchBrands } from '../../preferences/actions';
import { useCreateWishlist } from '../hooks/useCreateWishlist';

type CreateWishlistDialogProps = {
  isOpen: boolean;
  handleClose: (success?: boolean) => void;
  currentWishlists: string[];
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  preferencesPromise: Promise<FlatBuyerPreferences>;
};

export const CreateWishlistDialog: FC<CreateWishlistDialogProps> = ({
  currentWishlists,
  isOpen,
  handleClose,
  categoriesPromise,
  preferencesPromise,
}) => {
  const compositeCategories = use(categoriesPromise);
  const preferences = use(preferencesPromise);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('tablet'));
  const [brandInput, setBrandInput] = useState('');
  const [budgetInCents, setBudgetInCents] = useState<number | null>(null);
  const [percentOffMsrpClassificationId, setPercentOffMsrpClassificationId] = useState<
    number | null
  >(preferences.percentOffMsrpClassificationId || null);
  const [categories, setCategories] = useState<string[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState<string>('');

  /**
   * Very hacky/non-typesafe function to just get at the event.target.value of any event
   * and set our brandInput state to that value
   */
  const eventToBrandInput = (event: any) => {
    const target: string = event.target.value || '';
    setBrandInput(target);
  };

  const { createWishlist } = useCreateWishlist(
    {
      brandName: brandInput,
      budgetInCents,
      percentOffMsrpClassificationId,
      categories,
      additionalNotes,
    },
    {
      onSuccess: () => {
        analytics.track(AnalyticsEventNames.BUYER_WISHLIST_CREATED, {
          brand: brandInput,
          budgetInCents,
          percentOffMsrpClassificationId,
          additionalNotes,
        });
        handleClose(true);
      },
    },
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleClose()}
      fullScreen={isMobile}
      PaperProps={{
        sx: { ...DialogPaperSxProps, maxWidth: { mobile: 'none', smallTablet: 'desktop' } },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <DialogTitle>Create Wishlist</DialogTitle>
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <Cross />
        </IconButton>
      </Stack>
      <DialogContent>
        <Grid container gap={2} margin={0.5}>
          <Grid size={6}>
            <AsyncAutocomplete
              name="brand"
              label="Brand *"
              placeholder="e.g. Crocs"
              optionsFetcher={searchBrands}
              getOptionDisabled={(opt) => currentWishlists.includes(opt.label)}
              // note: using onInput here since this component is not controlled
              // so we have to monitor input changes
              onInput={eventToBrandInput}
              // note: using onBlur here since this component is not controlled
              // so we're just sort of hackily capturing the value when the focus leaves
              onBlur={eventToBrandInput}
              fullWidth
              freeSolo
            />
          </Grid>
          <Grid size={6}>
            <PercentOffMsrpSelector
              initialValue={percentOffMsrpClassificationId}
              label="Preferred Discount *"
              onChange={(event) => setPercentOffMsrpClassificationId(event.target.value || null)}
              optionLabel="percent"
            />
          </Grid>
          <Grid size={12}>
            <Stack direction={isMobile ? 'column' : 'row'} gap={0.5}>
              <Typography variant="bodyLargeHeavy">
                Additional info to assist with sourcing
              </Typography>
              <Typography variant="bodyLargeLight" color="typographyDarkSecondary">
                Optional
              </Typography>
            </Stack>
          </Grid>
          <Grid size={12}>
            <CurrencyInput
              name="budget"
              label={
                <Stack direction="row" gap={1}>
                  <Typography variant="labelSmall">Budget</Typography>
                  <Typography variant="labelSmall" color="typographyDarkSecondary">
                    Optional field
                  </Typography>
                </Stack>
              }
              initialPriceInCents={null}
              onChange={setBudgetInCents}
              placeholder="e.g. $2000"
              allowEmpty={true}
              fullWidth
            />
          </Grid>

          <Grid size={12}>
            <AccordionSelect
              label="Categories"
              placeholder="All"
              name="categories"
              variant="filled"
              defaultValue={[]}
              structuredOptions={compositeCategories}
              onChange={(selected) => {
                setCategories(selected);
              }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              minRows={isMobile ? 2 : 5}
              label={
                <Stack direction="row" gap={1}>
                  <Typography variant="labelSmall">Additional Notes</Typography>
                  <Typography variant="labelSmall" color="typographyDarkSecondary">
                    Optional field
                  </Typography>
                </Stack>
              }
              value={additionalNotes}
              onChange={(event) => setAdditionalNotes(event.target.value)}
              placeholder="Let us know if there are general categories, styles, quantities you're looking for"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ marginTop: 4 }}>
        <Button onClick={createWishlist} disabled={!brandInput}>
          Create Wishlist
        </Button>
      </DialogActions>
    </Dialog>
  );
};
